import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/Layout";
import { EnvBanner } from "@/components/EnvBanner";
import { Offers, useOffers } from "@/components/PartnerLive";
import { PartnerNav, HomeTab, PickupsTab, MapTab, HistoryTab, MeTab } from "@/components/PartnerTabs";
import { useLive } from "@/lib/live";
import { PartnerInstall, usePartnerManifest } from "@/components/PartnerInstall";
import { mobileInput, mobileProps, MobileHint, vehicleInput, VehicleHint } from "@/lib/india";
import { OtpBox } from "@/components/OtpBox";
import { useApp } from "@/context/AppContext";
import { pt } from "@/lib/pt";
import { PartnerHero } from "@/components/PartnerHero";
import { GoogleIcon } from "@/components/AuthPanel";
import { startGoogle } from "@/pages/AuthCallback";

const gLink = () => { try { return JSON.parse(sessionStorage.getItem("ks_partner_google") || "null"); } catch { return null; } };

const digits10 = (v) => v.replace(/\D/g, "").slice(-10);
const validMobile = (v) => /^[6-9]\d{9}$/.test(digits10(v));

const KEY = "ks_partner_token";
const shell = "mx-auto min-h-screen max-w-md space-y-4 bg-[#F4F8F5] px-4 pb-10 pt-5";
const appShell = "mx-auto min-h-screen max-w-md space-y-4 bg-[#F4F8F5] px-4 pb-28";

export function PartnerLogin() {
  usePartnerManifest();
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("otp");
  const [mob, setMob] = useState("");
  const gid = useApp().meta?.google_client_id;
  const [g, setG] = useState(gLink);
  const google = () => { localStorage.setItem("ks_google_for", "partner"); startGoogle(gid); };
  const saved = localStorage.getItem(KEY);
  if (saved) return <Navigate to={`/partner/${saved}`} replace />;
  const done = (data) => { sessionStorage.removeItem("ks_partner_google"); localStorage.setItem(KEY, data.token); nav(`/partner/${data.token}`, { replace: true }); };
  const onOtp = async (otp_token) => {
    try { const { data } = await api.post("/staff/otp-login", { otp_token, google_token: g?.google_token || "" }); done(data); }
    catch (err) { toast.error(errMsg(err)); setMode("otp"); }
  };
  const toJoin = () => { toast.info(pt("New partner? Please sign up first.")); nav(`/partner/join?mobile=${digits10(mob)}`); };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.username || !f.password) return toast.error("Please enter your user ID and password.");
    setBusy(true);
    try { const { data } = await api.post("/staff/login", f); done(data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (mode === "code") return <div className={shell}><Logo /><OtpBox mobile={digits10(mob)} purpose="login" who="partner" t={pt} onVerified={onOtp} onNotFound={toJoin} onBack={() => setMode("otp")} onGoogle={gid && !g ? google : undefined} /></div>;
  if (mode === "otp") return (
    <div className={shell}>
      <PartnerHero />
      <form onSubmit={(e) => { e.preventDefault(); if (!validMobile(mob)) return toast.error(pt("Please enter a 10-digit mobile number.")); setMode("code"); }}
        className="scroll-mt-4 space-y-4 rounded-3xl bg-white p-5 shadow-sm" id="partner-start" data-testid="partner-otp-form">
        <h2 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{pt(g ? "One last step" : "Log in or sign up")}</h2>
        {g ? <p className="rounded-2xl bg-[#E7F6EE] p-3 text-sm font-semibold text-[#1F7A50]" data-testid="partner-google-pending">{pt("Google account")}: {g.email}. {pt("Now verify your mobile number to finish.")}</p>
          : gid && <>
            <button type="button" onClick={google} data-testid="partner-google-btn"
              className="flex h-14 w-full items-center justify-center gap-3 rounded-2xl border-2 border-[#D2E0D8] bg-white text-lg font-bold text-[#0D3B2B] transition-transform active:scale-[.97]"><GoogleIcon />{pt("Continue with Google")}</button>
            <div className="flex items-center gap-3 text-xs font-semibold text-[#7B968B]"><span className="h-px flex-1 bg-[#D2E0D8]" />{pt("or")}<span className="h-px flex-1 bg-[#D2E0D8]" /></div>
          </>}
        <input value={mob} onChange={(e) => setMob(mobileInput(e.target.value))} placeholder={pt("Mobile number")} {...mobileProps}
          className="h-14 w-full rounded-xl border border-[#D2E0D8] px-4 text-lg" data-testid="partner-otp-mobile-input" />
        <MobileHint value={mob} />
        <button className="h-14 w-full rounded-2xl bg-[#0D3B2B] text-lg font-bold text-white transition-transform active:scale-[.97]" data-testid="partner-otp-send-btn">{pt("Send SMS code")}</button>
        <p className="text-center text-xs text-[#7B968B]">{pt("New here? We'll take you to sign up after the number check.")}</p>
        <Link to="/partner/join" className="block text-center text-base font-semibold text-[#2E9E6B]" data-testid="partner-join-link">{pt("New partner? Sign up")}</Link>
        {g && <button type="button" onClick={() => { sessionStorage.removeItem("ks_partner_google"); setG(null); }} className="block w-full text-center text-xs font-semibold text-[#7B968B]" data-testid="partner-google-cancel">{pt("Use a different Google account")}</button>}
        <button type="button" onClick={() => setMode("pw")} className="block w-full text-center text-xs font-semibold text-[#7B968B]" data-testid="partner-otp-password-link">{pt("Have a user ID? Log in with it")}</button>
      </form>
    </div>
  );
  return (
    <div className={shell}>
      <Logo />
      <form onSubmit={submit} className="space-y-4 rounded-3xl bg-white p-6" data-testid="partner-login-form">
        <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{pt("Log in with user ID")}</h1>
        <p className="text-sm text-[#4A6358]">{pt("Only if you created a user ID in your profile.")}</p>
        <input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="User ID" autoCapitalize="none" className="h-12 w-full rounded-xl border border-[#D2E0D8] px-4 text-base" data-testid="partner-username-input" />
        <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" className="h-12 w-full rounded-xl border border-[#D2E0D8] px-4 text-base" data-testid="partner-password-input" />
        <button disabled={busy} className="h-12 w-full rounded-2xl bg-[#0D3B2B] font-bold text-white disabled:opacity-60" data-testid="partner-login-submit-btn">{busy ? "Signing in…" : "Sign in"}</button>
        <button type="button" onClick={() => setMode("otp")} className="block w-full text-center text-sm font-semibold text-[#2E9E6B]" data-testid="partner-login-otp-btn">{pt("Back to mobile login")}</button>
      </form>
    </div>
  );
}

export function PartnerJoin() {
  const nav = useNavigate();
  const q = new URLSearchParams(window.location.search);
  const gj = gLink();
  const [f, setF] = useState({ name: gj?.name || "", mobile: q.get("mobile") || "", vehicle: "", ref_code: q.get("ref") || "" });
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(false);
  const [otpTok, setOtpTok] = useState("");
  const otpOn = !!useApp().meta?.otp_enabled;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const signup = async (otp_token) => {
    setBusy(true);
    try { const { data } = await api.post("/staff/signup", { ...f, otp_token, google_token: gj?.google_token || "" }); sessionStorage.removeItem("ks_partner_google"); localStorage.setItem(KEY, data.token); toast.success("Account created. Now upload your KYC documents."); nav(`/partner/${data.token}`, { replace: true }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!validMobile(f.mobile)) return toast.error(pt("Please enter a 10-digit mobile number."));
    if (!f.name.trim()) return toast.error(pt("Please enter your full name."));
    if (otpOn && !otpTok) return setCode(true);
    signup(otpTok);
  };
  const onOtp = (t) => { setOtpTok(t); setCode(false); return signup(t); };
  const field = "h-12 w-full rounded-xl border border-[#D2E0D8] px-4 text-base";
  if (code) return <div className={shell}><Logo /><OtpBox mobile={digits10(f.mobile)} purpose="signup" who="partner" t={pt} onVerified={onOtp} onBack={() => setCode(false)} /></div>;
  return (
    <div className={shell}>
      <Logo />
      <form onSubmit={submit} className="space-y-3 rounded-3xl bg-white p-6" data-testid="partner-join-form">
        <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{pt("Become a Kindsaver pickup partner")}</h1>
        <p className="text-sm text-[#4A6358]">{pt("Sign up, then upload your ID documents. You'll start getting pickups after our team approves your KYC and police verification.")}</p>
        <input value={f.name} onChange={set("name")} placeholder={pt("Full name")} className={field} data-testid="join-name-input" />
        <input value={f.mobile} onChange={(e) => { setOtpTok(""); setF({ ...f, mobile: mobileInput(e.target.value) }); }} placeholder={pt("Mobile number")} {...mobileProps} className={field} data-testid="join-mobile-input" />
        <MobileHint value={f.mobile} />
        <input value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: vehicleInput(e.target.value) })} placeholder={pt("Vehicle number e.g. RJ14AB1234 (optional)")} autoCapitalize="characters" className={field} data-testid="join-vehicle-input" />
        <VehicleHint value={f.vehicle} />
        <input value={f.ref_code} onChange={set("ref_code")} placeholder={pt("Referral code (optional)")} className={field} data-testid="join-ref-input" />
        <button disabled={busy} className="h-12 w-full rounded-2xl bg-[#0D3B2B] font-bold text-white disabled:opacity-60" data-testid="join-submit-btn">{busy ? "…" : pt(otpOn ? "Verify mobile & sign up" : "Sign up")}</button>
        <Link to="/partner" className="block text-center text-sm font-semibold text-[#2E9E6B]" data-testid="join-login-link">{pt("Already a partner? Log in")}</Link>
      </form>
    </div>
  );
}

export default function Partner() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pos, setPos] = useState(null);
  const [online, setOnline] = useState(false);
  const live = useRef(null);
  const onlineRef = useRef(false);
  onlineRef.current = online;
  const [offers, loadOffers] = useOffers(token);
  useEffect(() => {
    if (!navigator.geolocation) return setPos(false);
    navigator.geolocation.getCurrentPosition((p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }), () => setPos(false), { timeout: 8000 });
    const w = navigator.geolocation.watchPosition((p) => { live.current = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }; }, () => {}, { enableHighAccuracy: true });
    let last = 0;
    const send = () => {
      if (!live.current || Date.now() - last < (onlineRef.current ? 14000 : 59000)) return;
      last = Date.now();
      api.post(`/partner/${token}/location`, live.current).catch(() => {});
    };
    const first = setTimeout(send, 3000);
    const id = setInterval(send, 5000);
    const wake = () => { if (document.visibilityState === "visible") { last = 0; send(); } };
    document.addEventListener("visibilitychange", wake);
    return () => { navigator.geolocation.clearWatch(w); clearTimeout(first); clearInterval(id); document.removeEventListener("visibilitychange", wake); };
  }, [token]);
  const load = useCallback(() => api.get(`/partner/${token}`, { params: pos || {} }).then((r) => { setData(r.data); setOnline(r.data.online); setErr(""); }).catch((e) => {
    if (e?.response?.status === 404) {
      if (localStorage.getItem(KEY) === token) localStorage.removeItem(KEY);
      toast.error(pt("This partner account is no longer active. Please log in or sign up again."), { id: "partner-stale" });
      return nav("/partner", { replace: true });
    }
    setErr(errMsg(e));
  }), [token, pos]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const t = setInterval(() => document.visibilityState === "visible" && load(), 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(loadOffers, 8000); return () => clearInterval(t); }, [loadOffers]);
  usePartnerManifest();
  useLive({ role: "partner", token }, (e) => { if (e.event === "offer") toast.info("New pickup request near you!"); loadOffers(); load(); });

  const onTrip = async (j, status) => {
    if (!window.confirm(`Mark ${j.booking_id} as "${status}"? The customer will be notified.`)) return;
    try { await api.post(`/partner/${token}/bookings/${j.booking_id}/trip`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const logout = () => { localStorage.removeItem(KEY); nav("/partner", { replace: true }); };

  const [tab, setTab] = useState("home");
  const onOnline = (v) => { setOnline(v); if (v && live.current) api.post(`/partner/${token}/location`, live.current).catch(() => {}); };
  const T = { home: HomeTab, pickups: PickupsTab, map: MapTab, history: HistoryTab, me: MeTab }[tab];

  return (
    <div className={appShell}>
      <EnvBanner />
      <header className="sticky top-0 z-40 -mx-4 flex h-14 items-center justify-between border-b border-[#D2E0D8] bg-[#F4F8F5]/90 px-4 backdrop-blur-md">
        <Logo />
        <div className="flex items-center gap-2">
        <PartnerInstall />
        <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${online ? "bg-[#2E9E6B]/15 text-[#2E9E6B]" : "bg-[#EAEFEA] text-[#7B968B]"}`} data-testid="partner-header-status">
          <span className={`h-2 w-2 rounded-full ${online ? "bg-[#2E9E6B]" : "bg-[#7B968B]"}`} />{online ? "Online" : "Offline"}</span>
        </div>
      </header>
      {err ? <p className="rounded-2xl bg-[#FEE2E2] p-4 text-[#991B1B]" data-testid="partner-error">{err}</p> : !data ? <p className="text-[#7B968B]">Loading…</p> : (
        <>
          <div key={tab} className="ks-page">
            <T data={data} pos={pos} token={token} online={online} onOnline={onOnline} go={setTab} onTrip={onTrip} reload={load} logout={logout} />
          </div>
          <Offers offers={offers} token={token} reload={() => { loadOffers(); load(); }} />
          <PartnerNav tab={tab} setTab={setTab} count={data.jobs.length} />
        </>
      )}
    </div>
  );
}
